import { useEffect, useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import { ImagePlus, Loader2, X, ArrowLeft, Link2, Edit3, Gift } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import {
  orderCategories,
  CATEGORIES,
  type WishlistCategory,
  type WishlistItem,
} from '@/lib/wishlist'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLanguage } from '@/context/LanguageContext'

interface ItemFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  item?: WishlistItem | null
  wishlistId?: string | null
  kind?: 'wishlist' | 'guide'
  onSaved: (item: WishlistItem) => void
}

const notifyBoardFollowers = (wishlistId: string | null | undefined) => {
  if (!wishlistId) return
  supabase.functions.invoke('notify-board-followers', { body: { wishlist_id: wishlistId } }).catch((e) => console.error('notify-board-followers failed', e))
}

interface Metadata {
  title: string | null
  description: string | null
  image: string | null
  images: string[] | null
  siteName: string | null
  brand: string | null
  price: number | { current: number | null; original: number | null; currency: string; symbol: string } | null
  originalPrice?: number | null
  category: string | string[] | null
  url: string
  finalUrl?: string
  urlParams?: { size: string | null; color: string | null } | null
  aiEnhanced?: boolean
  blocked?: boolean
}

const CATEGORY_KEYWORDS: Array<[string, string[]]> = [
  ['Tecnologia', ['tech', 'elettronica', 'electronic', 'cuffie', 'smartphone', 'telefono', 'computer', 'laptop', 'tablet', 'console', 'auricolari', 'smartwatch', 'speaker', 'audio', 'fotocamera', 'camera', 'drone', 'monitor', 'tv', 'televis']],
  ['Arredo casa', ['casa', 'home', 'arred', 'cucina', 'kitchen', 'living', 'decor', 'mobil', 'lampad', 'letto', 'bagno', 'giardin', 'garden', 'tavol', 'sedie', 'divan', 'profumat', 'candela', 'candle']],
  ['Abbigliamento', ['abbigl', 'cloth', 'maglia', 'shirt', 'pantal', 'pant', 'jeans', 'felpa', 'hoodie', 'giacca', 'jacket', 'cappott', 'coat', 'vestit', 'dress', 'gonna', 'skirt', 'scarpe', 'shoe', 'sneaker', 'stival', 'boot', 'sandalo', 'sandal', 'tacco', 'calzar']],
  ['Gioielli', ['gioiell', 'jewel', 'orecchin', 'earring', 'collan', 'necklace', 'anell', 'ring', 'braccial', 'bracelet', 'braccialetto', 'orolog', 'watch', 'pendent', 'pendant', 'spilla', 'brooch']],
  ['Sport', ['sport', 'fitness', 'palestra', 'calcio', 'tennis', 'bici', 'bike', 'ciclismo', 'corsa', 'running', 'nuoto', 'yoga', 'trekking', 'campeggio', 'outdoor', 'scii', 'ski', 'allenamento', 'gym']],
  ['Accessori', ['accessor', 'accessory', 'borsa', 'bag', 'cintur', 'belt', 'occhial', 'scarpe', 'shoe', 'portafogl', 'wallet', 'ombrell', 'umbrella', 'chiav', 'keychain', 'zaino', 'backpack', 'cappel', 'hat', 'sunglass', 'sciarpa', 'scarf', 'guant', 'glove']],
]

/** Keeps only hex digits and always stores the value as "#rrggbb" (or empty = automatic). */
function normalizeHexInput(v: string): string {
  const raw = v.replace(/[^0-9a-fA-F]/g, '').slice(0, 6)
  return raw ? `#${raw}` : ''
}

function guessCategoryFallback(hints: Array<string | null>): string {
  const haystack = hints
    .filter((h): h is string => !!h)
    .join(' ')
    .toLowerCase()
  if (!haystack) return 'Accessori'
  for (const [category, keywords] of CATEGORY_KEYWORDS) {
    if (keywords.some((k) => haystack.includes(k))) return category
  }
  return 'Accessori'
}

/** Same photo served at different sizes/CDN transforms must count as one. */
function imageIdentity(raw: string): string {
  try {
    const parsed = new URL(raw)
    let path = parsed.pathname.toLowerCase()
    path = path.replace(/\._[a-z0-9,_]+_\./i, '.')
    path = path.replace(/[_-]\d{2,4}x\d{2,4}(?=[._-]|$)/g, '')
    path = path.replace(/[_-]\d{2,4}[wh](?=[._-]|$)/g, '')
    path = path.replace(/@\d+x(?=[._-]|$)/g, '')
    path = path.replace(/\/(?:w|h|c|q|f|dpr|ar)_[^/]+/g, '')
    path = path.replace(/\/(?:thumb|thumbs|small|medium|large|xl|zoom|resize|cache)\//g, '/')
    path = path.replace(/\.(jpg|jpeg|png|webp|avif|bmp|tiff?)$/i, '')
    path = path.replace(/[_-]+$/g, '')
    return `${parsed.hostname.replace(/^www\./, '')}${path}`
  } catch {
    return raw.toLowerCase()
  }
}

/** Verifies an image really loads in the browser (skips 404s, HTML pages, hotlink blocks). */
function imageLoads(url: string, timeout = 8000): Promise<boolean> {
  return new Promise((resolve) => {
    const img = new Image()
    let settled = false
    const done = (ok: boolean) => {
      if (settled) return
      settled = true
      img.onload = null
      img.onerror = null
      clearTimeout(timer)
      resolve(ok)
    }
    const timer = setTimeout(() => done(false), timeout)
    img.onload = () => done(img.naturalWidth > 1 && img.naturalHeight > 1)
    img.onerror = () => done(false)
    img.referrerPolicy = 'no-referrer'
    img.src = url
  })
}

/** Keeps only the images that actually render, preserving the original order. */
async function keepLoadableImages(urls: string[]): Promise<string[]> {
  const results = await Promise.all(urls.map((u) => imageLoads(u)))
  return urls.filter((_, i) => results[i])
}

function dedupeImages(urls: string[]): string[] {
  const seen = new Set<string>()
  const output: string[] = []
  for (const url of urls) {
    const key = imageIdentity(url)
    if (seen.has(key)) continue
    seen.add(key)
    output.push(url)
  }
  return output
}

const inputClass =
  'w-full rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none transition focus:border-neutral-900 focus:ring-2 focus:ring-neutral-900/10'

const MAX_IMAGES = 5

export default function ItemFormDialog({
  open,
  onOpenChange,
  userId,
  item,
  wishlistId,
  kind = 'wishlist',
  onSaved,
}: ItemFormDialogProps) {
  const isGuide = kind === 'guide'
  const [customCategories, setCustomCategories] = useState<WishlistCategory[]>([])
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<string>('Accessori')
  const [size, setSize] = useState('')
  const [price, setPrice] = useState('')
  const [originalPrice, setOriginalPrice] = useState<number | null>(null)
  const [color1, setColor1] = useState('')
  const [color2, setColor2] = useState('')
  const [colorText, setColorText] = useState('')
  const [bgColor, setBgColor] = useState('')
  const [url, setUrl] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [galleryImages, setGalleryImages] = useState<string[]>([])
  const [importing, setImporting] = useState(false)
  const [importError, setImportError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [step, setStep] = useState<'link' | 'details'>('link')
  const [linkInput, setLinkInput] = useState('')
  const [isRecurring, setIsRecurring] = useState(false)
  const { language } = useLanguage()
  const isEn = language === 'en'
  const t = {
    importError: isEn ? "I can't read this link: feel free to fill it in manually." : 'Non riesco a leggere questo link: compila pure a mano.',
    blockedError: isEn ? 'This website blocks automatic import. Please fill in the fields below manually.' : "Questo sito blocca l'importazione automatica. Compila i campi qui sotto a mano.",
    titleRequired: isEn ? 'The title is required' : "Il titolo e' obbligatorio",
    productPhoto: isEn ? 'Product photo' : 'Foto del prodotto',
    mainBadge: isEn ? 'MAIN' : 'PRINCIPALE',
    mainAlt: isEn ? 'Main' : 'Principale',
    removeImage: isEn ? 'Remove image' : 'Rimuovi immagine',
    setMain: isEn ? 'Set as main' : 'Imposta come principale',
    remove: isEn ? 'Remove' : 'Rimuovi',
    firstPhotoHint: isEn ? 'The first photo is the cover. Click on another one to change it.' : "La prima foto è la copertina. Clicca su un'altra per cambiarla.",
    uploading: isEn ? 'Uploading...' : 'Caricamento...',
    titleLabel: isEn ? 'Title*' : 'Titolo*',
    titlePlaceholder: isEn ? 'E.g. Wireless headphones' : 'Es. Cuffie wireless',
    descriptionLabel: isEn ? 'Description' : 'Descrizione',
    descriptionPlaceholder: isEn ? 'If you like, add a few extra details about the product or why you would like to receive this gift!' : 'Se lo desideri, fornisci qualche dettaglio in più sul prodotto o perché vorresti ricevere questo regalo!',
    categoryLabel: isEn ? 'Category' : 'Categoria',
    priceLabel: isEn ? 'Price' : 'Prezzo',
    sizeLabel: isEn ? 'Desired size' : 'Misura desiderata',
    sizePlaceholder: isEn ? 'E.g. Size M, 42, 180 cm...' : 'Es. Taglia M, 42, 180 cm...',
    colorVariantLabel: isEn ? 'Color variant' : 'Variante di colore',
    colorVariantDesired: isEn ? 'desired' : 'desiderata',
    colorTextPlaceholder: isEn ? 'Red, Dark blue...' : 'Rosso, Blu scuro...',
    colorPickerLabel: isEn ? 'Color picker' : 'Selettore colori',
    color1Title: isEn ? 'Color 1' : 'Colore 1',
    color2Title: isEn ? 'Color 2 (optional)' : 'Colore 2 (opzionale)',
    removeColor1: isEn ? 'Remove first color' : 'Rimuovi primo colore',
    removeColor2: isEn ? 'Remove second color' : 'Rimuovi secondo colore',
    optionalPlaceholder: isEn ? '(optional)' : '(opzionale)',
    bgColorLabel: isEn ? 'Photo background color' : 'Colore di sfondo foto',
    bgColorHint: isEn ? "Choose the color behind the photo if the automatic one isn't correct." : 'Scegli il colore dietro la foto se quello automatico non è corretto.',
    bgColorTitle: isEn ? 'Background color' : 'Colore di sfondo',
    autoDetectTitle: isEn ? 'Back to automatic detection' : 'Torna al rilevamento automatico',
    autoPlaceholder: isEn ? '(automatic)' : '(automatico)',
    recurringTitle: isEn ? 'Recurring gift' : 'Regalo ricorrente',
    recurringDesc: isEn ? 'Turn it on for gifts that can be purchased more than once. It will stay available in the wishlist and show the number of purchases.' : 'Attivalo per regali che possono essere acquistati più volte. Rimarrà disponibile nella wishlist e mostrerà il numero di acquisti.',
    storeLinkLabel: isEn ? 'Store link' : 'Link del negozio',
    saving: isEn ? 'Saving...' : 'Salvataggio...',
    saveChanges: isEn ? 'Save changes' : 'Salva modifiche',
    add: isEn ? 'Add' : 'Aggiungi',
  }

  useEffect(() => {
    if (!open || !userId) return
    supabase
      .from('wishlist_categories')
      .select('*')
      .eq('user_id', userId)
      .order('position', { ascending: true })
      .then(({ data }) =>
        setCustomCategories((data ?? []) as unknown as WishlistCategory[])
      )
  }, [open, userId])

  const availableCategories = orderCategories(
    Array.from(new Set([...CATEGORIES, ...customCategories.map((c) => c.name)])),
    customCategories
  )

  useEffect(() => {
    if (!open) return
    setTitle(item?.title ?? '')
    setDescription(item?.description ?? '')
    setCategory(item?.category ?? 'Accessori')
    setSize(item?.size ?? '')
    setPrice(item?.price != null ? String(item.price) : '')
    setOriginalPrice((item as any)?.original_price ?? null)
    const savedColor = (item as any)?.color ?? ''
    const colorParts = savedColor.split(/[,/]/).map((s: string) => s.trim()).filter(Boolean)
    setColor1(colorParts[0] ?? '')
    setColor2(colorParts[1] ?? '')
    setColorText((item as any)?.color_text ?? '')
    setBgColor((item as any)?.bg_color ?? '')
    setUrl(item?.url ?? '')
    setImageUrl(item?.image_url ?? '')
    setGalleryImages([])
    setLinkInput(item?.url ?? '')
    setIsRecurring(item?.is_recurring ?? false)
    setImportError(null)
    setError(null)
    setImporting(false)
    setUploading(false)
    setSaving(false)
    setStep(item ? 'details' : 'link')

    // Load existing gallery images when editing
    if (item) {
      supabase
        .from('wishlist_item_images')
        .select('url')
        .eq('item_id', item.id)
        .order('position', { ascending: true })
        .then(({ data }) => {
          if (data) {
            const urls = (data as unknown as { url: string }[]).map((g) => g.url)
            setGalleryImages(urls)
          }
        })
    }
  }, [open, item])

  const importFromUrl = async (targetUrl: string) => {
    const target = targetUrl.trim()
    if (!target || importing) return
    setImporting(true)
    setImportError(null)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('fetch-url-metadata', {
        body: { url: target },
      })
      if (fnError || !data) {
        setImportError(t.importError)
        setUrl(target)
        setStep('details')
        return
      }
      const meta = data as Metadata

      // Blocked site: show a specific message and let the user fill manually
      if (meta.blocked) {
        setImportError(t.blockedError)
        setUrl(target)
        setStep('details')
        return
      }

      const MAX_TITLE = 30

      let finalTitle = meta.title ?? ''
      if (finalTitle.length > MAX_TITLE) finalTitle = finalTitle.slice(0, MAX_TITLE)

      if (finalTitle) setTitle(finalTitle)
      setUrl(meta.url || target)
      const isImageUrl = (u: string | null | undefined): u is string => {
        if (!u || !/^https?:\/\//i.test(u)) return false
        if (/\.(svg|ico)(\?|$)/i.test(u)) return false
        // Accept extension-less CDN URLs too (Shopify, Cloudinary, Amazon...)
        return true
      }
      const pageUrls = new Set(
        [target, meta.url, meta.finalUrl]
          .filter(Boolean)
          .map((u) => (u as string).split('#')[0].toLowerCase())
      )
      const uniqueImages = dedupeImages([
        ...(isImageUrl(meta.image) ? [meta.image] : []),
        ...(meta.images ?? []).filter(isImageUrl),
      ]).filter((u) => !pageUrls.has(u.split('#')[0].toLowerCase()))
      // Drop anything that doesn't actually render (404, hotlink block, HTML page).
      const validImages = await keepLoadableImages(uniqueImages.slice(0, MAX_IMAGES + 5))
      setImageUrl(validImages[0] ?? '')
      setGalleryImages(validImages.slice(1, MAX_IMAGES))
      const priceIsObject = meta.price !== null && typeof meta.price === 'object'
      const detectedPrice = priceIsObject ? (meta.price as { current: number | null }).current : meta.price
      const detectedOriginalPrice = priceIsObject ? (meta.price as { original: number | null }).original : meta.originalPrice
      const detectedCategories = Array.isArray(meta.category) ? meta.category : meta.category ? [meta.category] : []
      if (detectedPrice != null) setPrice(String(detectedPrice))
      setOriginalPrice(detectedOriginalPrice ?? null)
      const categoryHint = detectedCategories.join(' ')
      const hints = [categoryHint || null, finalTitle, meta.siteName]
      const matchedCategory = availableCategories.find((available) =>
        detectedCategories.some((detected) => detected.toLowerCase().includes(available.toLowerCase()))
      )
      // Auto-fill size and color from URL params if detected
      if (meta.urlParams?.size) setSize(meta.urlParams.size)
      if (meta.urlParams?.color) setColorText(meta.urlParams.color)
      setCategory(matchedCategory || guessCategoryFallback(hints))
      setStep('details')
    } catch {
      setImportError(t.importError)
      setUrl(target)
      setStep('details')
    } finally {
      setImporting(false)
    }
  }

  const handleLinkSubmit = (e: FormEvent) => {
    e.preventDefault()
    const target = linkInput.trim()
    if (!target) return
    importFromUrl(target)
  }

  const skipToManual = () => {
    if (linkInput.trim()) setUrl(linkInput.trim())
    setStep('details')
  }

  const totalImages = (imageUrl ? 1 : 0) + galleryImages.filter(Boolean).length
  const canAddImages = totalImages < MAX_IMAGES

  const uploadGalleryImages = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length === 0 || uploading) return
    const remaining = MAX_IMAGES - ((imageUrl ? 1 : 0) + galleryImages.filter(Boolean).length)
    if (remaining <= 0) return
    const filesToUpload = files.slice(0, remaining)
    setUploading(true)
    setError(null)
    try {
      const uploaded: string[] = []
      for (const file of filesToUpload) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
        const path = `${userId}/${crypto.randomUUID()}-${safeName}`
        const { error: upError } = await supabase.storage
          .from('wishlist-images')
          .upload(path, file)
        if (!upError) {
          const { data } = supabase.storage.from('wishlist-images').getPublicUrl(path)
          uploaded.push(data.publicUrl)
        }
      }
      if (uploaded.length > 0) {
        // Auto-promote: if no main image yet, first uploaded becomes main
        if (!imageUrl) {
          setImageUrl(uploaded[0])
          setGalleryImages(prev => [...prev, ...uploaded.slice(1)])
        } else {
          setGalleryImages(prev => [...prev, ...uploaded])
        }
      }
    } finally {
      setUploading(false)
    }
  }

  const save = async (e: FormEvent) => {
    e.preventDefault()
    if (saving || importing || uploading) return
    if (!title.trim()) {
      setError(t.titleRequired)
      return
    }
    setSaving(true)
    setError(null)
    const payload = {
      title: title.trim(),
      description: description.trim() || null,
      category,
      size: size.trim() || null,
      url: url.trim() || null,
      image_url: imageUrl.trim() || null,
      price: price ? Number(price) : null,
      original_price: originalPrice,
      color: [color1.trim(), color2.trim()].filter(Boolean).join(', ') || null,
      color_text: colorText.trim() || null,
      bg_color: bgColor.trim() || null,
      is_recurring: isRecurring,
    }
    const query = item
      ? supabase.from('wishlist_items').update(payload).eq('id', item.id)
      : supabase
          .from('wishlist_items')
          .insert({ ...payload, user_id: userId, wishlist_id: wishlistId ?? null })
    const { data, error: err } = await query.select().single()
    if (err) {
      setSaving(false)
      setError(err.message)
      return
    }
    const savedItem = data as unknown as WishlistItem

    // Always sync the gallery: delete old entries and re-insert the current set.
    // This handles adds, removals, reordering, and clearing all extra images.
    if (item) {
      await supabase.from('wishlist_item_images').delete().eq('item_id', savedItem.id)
    }
    const allImages = [imageUrl, ...galleryImages].filter(Boolean) as string[]
    const galleryToSave = allImages.slice(1).map((imgUrl, idx) => ({
      item_id: savedItem.id,
      url: imgUrl,
      position: idx,
    }))
    if (galleryToSave.length > 0) {
      await supabase.from('wishlist_item_images').insert(galleryToSave)
    }

    setSaving(false)
    supabase.functions
      .invoke('notify-board-followers', { body: { wishlist_id: savedItem.wishlist_id } })
      .catch((e) => console.error('notify-board-followers failed', e))
    onSaved(savedItem)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-lg max-w-lg max-h-[90vh] overflow-y-auto glass-strong block">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold text-neutral-900 flex items-center gap-2">
            {item ? (
              <>
                <Edit3 className="h-5 w-5 text-indigo-500" />{' '}
                {isGuide
                  ? language === 'en' ? 'Edit recommendation' : 'Modifica consiglio'
                  : language === 'en' ? 'Edit wish' : 'Modifica desiderio'}
              </>
            ) : (
              <>
                <Gift className="h-5 w-5 text-indigo-500" />{' '}
                {isGuide
                  ? language === 'en' ? 'New recommendation' : 'Nuovo consiglio'
                  : language === 'en' ? 'New wish' : 'Nuovo desiderio'}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 'link' && (
          <div className="mt-2">
            <form onSubmit={handleLinkSubmit} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-semibold text-neutral-800 flex items-center gap-1.5">
                  <Link2 className="h-4 w-4 text-indigo-500" />
                  {language === 'en' ? 'Paste the product link' : 'Incolla il link del prodotto'}
                </label>
                <p className="mb-3 text-xs text-neutral-500">
                  {language === 'en' ? 'Paste the product link and I\'ll fill it in automatically: title, photo, price and category.' : 'Incolla il link del prodotto e lo riempio automaticamente: titolo, foto, prezzo e categoria.'}
                </p>
                <input
                  type="url"
                  autoFocus
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  placeholder="https://..."
                  className={inputClass}
                />
                {importError && <p className="mt-2 text-xs text-red-600">{importError}</p>}
              </div>

              <button
                type="submit"
                disabled={importing || !linkInput.trim()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800 disabled:opacity-50"
              >
                {importing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {language === 'en' ? 'Fetching data...' : 'Recupero dati...'}
                  </>
                ) : (
                  language === 'en' ? 'Fetch data from link' : 'Recupera dati dal link'
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-neutral-200" />
                <span className="text-xs text-neutral-400">{language === 'en' ? 'or' : 'oppure'}</span>
                <div className="h-px flex-1 bg-neutral-200" />
              </div>

              <button
                type="button"
                onClick={skipToManual}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50"
              >
                {language === 'en' ? 'Enter everything manually' : 'Inserisci tutto a mano'}
              </button>
            </form>
          </div>
        )}

        {step === 'details' && (
          <>
            {!item && (
              <button
                type="button"
                onClick={() => setStep('link')}
                className="mb-2 inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-900 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                {language === 'en' ? 'Back to link' : 'Torna al link'}
              </button>
            )}

            <div className="mb-4">
                <p className="mb-2 text-xs font-semibold text-neutral-700">
                  {t.productPhoto}{' '}
                  <span className="font-normal text-neutral-400">
                    ({totalImages}/{MAX_IMAGES})
                  </span>
                </p>

                {(imageUrl || galleryImages.length > 0) ? (
                  <>
                    {/* Main photo — full width on mobile, sits above gallery */}
                    {imageUrl && (
                      <div className="mb-2">
                        <div className="relative overflow-hidden rounded-xl border-2 border-neutral-900 ring-2 ring-neutral-900/20 bg-neutral-50">
                          <img
                            src={imageUrl}
                            alt={t.mainAlt}
                            className="h-40 w-full object-contain"
                          />
                          <span className="absolute left-1.5 top-1.5 rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-bold text-white">
                            {t.mainBadge}
                          </span>
                          <button
                            type="button"
                            onClick={() => setImageUrl('')}
                            className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow transition hover:text-red-500"
                            aria-label={t.removeImage}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    )}
                    {/* Gallery photos — separate grid below main photo */}
                    {galleryImages.filter(Boolean).length > 0 && (
                      <div className="grid grid-cols-4 gap-2">
                        {galleryImages.filter(Boolean).map((img, idx) => (
                          <div key={idx} className="group relative">
                            <button
                              type="button"
                              onClick={() => {
                                const prevMain = imageUrl
                                setImageUrl(img)
                                setGalleryImages(prev => {
                                  const filtered = prev.filter(Boolean)
                                  if (prevMain) {
                                    filtered[idx] = prevMain
                                  } else {
                                    filtered.splice(idx, 1)
                                  }
                                  return filtered
                                })
                              }}
                              className="block w-full overflow-hidden rounded-xl border-2 border-transparent transition hover:border-neutral-900 bg-neutral-50"
                              title={t.setMain}
                            >
                              <img
                                src={img}
                                alt={isEn ? `Option ${idx + 1}` : `Opzione ${idx + 1}`}
                                className="h-40 w-full object-contain"
                                referrerPolicy="no-referrer"
                                onError={() =>
                                  setGalleryImages(prev => prev.filter(u => u !== img))
                                }
                              />
                            </button>
                            <button
                              type="button"
                              onClick={() => setGalleryImages(prev => prev.filter((_, i) => i !== idx))}
                              className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow transition hover:text-red-500"
                              aria-label={t.remove}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-xs text-neutral-400">
                      {t.firstPhotoHint}
                    </p>
                  </>
                ) : null}

                {canAddImages ? (
                  <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-neutral-100/50 px-4 py-3 text-xs font-semibold text-neutral-700 transition hover:border-neutral-300 hover:bg-neutral-100">
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    {uploading ? t.uploading : isEn ? `Add photo (${totalImages}/${MAX_IMAGES})` : `Aggiungi foto (${totalImages}/${MAX_IMAGES})`}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={uploadGalleryImages}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                ) : (
                  <p className="mt-2 text-center text-xs text-neutral-400">{isEn ? `Maximum of ${MAX_IMAGES} photos reached` : `Massimo ${MAX_IMAGES} foto raggiunto`}</p>
                )}
              </div>

            <form onSubmit={save} className="space-y-4">
              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-semibold text-slate-800">
                    {t.titleLabel}
                  </label>
                  <span className={`text-xs ${title.length > 30 ? 'text-red-500' : 'text-neutral-400'}`}>
                    {title.length}/30
                  </span>
                </div>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value.slice(0, 30))}
                  required
                  maxLength={30}
                  placeholder={t.titlePlaceholder}
                  className={inputClass}
                />
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="block text-sm font-semibold text-slate-800">
                    {isGuide
                      ? language === 'en' ? 'Why do you recommend it?' : 'Perché lo consigli?'
                      : t.descriptionLabel}
                  </label>
                  <span className={`text-xs ${description.length > 340 ? 'text-red-500' : 'text-neutral-400'}`}>
                    {description.length}/340
                  </span>
                </div>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 340))}
                  rows={3}
                  maxLength={340}
                  placeholder={
                    isGuide
                      ? language === 'en'
                        ? 'Describe briefly why you recommend this product.'
                        : 'Descrivi brevemente perché consigli questo prodotto.'
                      : t.descriptionPlaceholder
                  }
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-800">
                    {t.categoryLabel}
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={`form-select-arrow ${inputClass}`}
                  >
                    {availableCategories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                {isGuide ? (
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-800">
                      {t.priceLabel}
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="EUR"
                      className={inputClass}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="mb-1 block text-sm font-semibold text-slate-800">
                      {t.sizeLabel}
                    </label>
                    <input
                      value={size}
                      onChange={(e) => setSize(e.target.value)}
                      placeholder={t.sizePlaceholder}
                      className={inputClass}
                    />
                  </div>
                )}
              </div>

              {!isGuide && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-800">
                    {t.priceLabel}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    placeholder="EUR"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold text-slate-800">
                    {t.colorVariantLabel} <span className="md:inline hidden">{t.colorVariantDesired}</span>
                  </label>
                  <input
                    type="text"
                    value={colorText}
                    onChange={(e) => setColorText(e.target.value)}
                    placeholder={t.colorTextPlaceholder}
                    maxLength={50}
                    className={inputClass}
                  />
                </div>
              </div>
              )}

              {!isGuide && (
              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">
                  {t.colorPickerLabel}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                        value={/^#[0-9a-f]{6}$/i.test(color1) ? color1 : '#ffffff'}
                      onChange={(e) => setColor1(e.target.value)}
                      className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-neutral-200 bg-white p-1"
                      title={t.color1Title}
                    />
                    <input
                      value={color1}
                      onChange={(e) => setColor1(normalizeHexInput(e.target.value))}
                      placeholder="#dc2626"
                      className={`${inputClass} flex-1`}
                    />
                    {color1 && (
                      <button
                        type="button"
                        onClick={() => setColor1('')}
                        className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-400 transition hover:text-red-500 hover:border-red-200"
                        title={t.removeColor1}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={/^#[0-9a-f]{6}$/i.test(color2) ? color2 : '#ffffff'}
                      onChange={(e) => setColor2(e.target.value)}
                      className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-neutral-200 bg-white p-1"
                      title={t.color2Title}
                    />
                    <input
                      value={color2}
                      onChange={(e) => setColor2(normalizeHexInput(e.target.value))}
                      placeholder={t.optionalPlaceholder}
                      className={`${inputClass} flex-1`}
                    />
                    {color2 && (
                      <button
                        type="button"
                        onClick={() => setColor2('')}
                        className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-400 transition hover:text-red-500 hover:border-red-200"
                        title={t.removeColor2}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">
                  {t.bgColorLabel}
                </label>
                <p className="mb-2 text-xs text-neutral-500">
                  {t.bgColorHint}
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-f]{6}$/i.test(bgColor) ? bgColor : '#ffffff'}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="h-10 w-12 shrink-0 cursor-pointer rounded-lg border border-neutral-200 bg-white p-1"
                    title={t.bgColorTitle}
                  />
                  <input
                    value={bgColor}
                    onChange={(e) => setBgColor(normalizeHexInput(e.target.value))}
                    placeholder={t.autoPlaceholder}
                    className={`${inputClass} flex-1`}
                  />
                  {bgColor && (
                    <button
                      type="button"
                      onClick={() => setBgColor('')}
                      className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-400 transition hover:text-red-500 hover:border-red-200"
                      title={t.autoDetectTitle}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {!isGuide && (
              <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${isRecurring ? 'border-indigo-300 bg-indigo-50' : 'border-neutral-200 bg-white hover:border-neutral-300'}`}>
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-neutral-300 accent-indigo-600"
                />
                <span>
                  <span className="block text-sm font-bold text-neutral-900">{t.recurringTitle}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-neutral-500">
                    {t.recurringDesc}
                  </span>
                </span>
              </label>
              )}

              <div>
                <label className="mb-1 block text-sm font-semibold text-slate-800">
                  {isGuide
                    ? language === 'en' ? 'Store link' : 'Link al negozio'
                    : t.storeLinkLabel}
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://..."
                  className={inputClass}
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                type="submit"
                disabled={saving || importing || uploading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-neutral-800 disabled:opacity-50"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? t.saving : item ? t.saveChanges : t.add}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
